import React, { useState, useEffect, useMemo } from 'react';
import useEscapeKey from '../../../hooks/useEscapeKey';
import Modal from '../../../components/Modal';
import { getEmployeeDisplayName } from '../utils/helpers';
import { API_BASE } from '../utils/constants';

// Helper function to check if a policy is usage-only
const isUsageOnlyPolicy = (policy) => {
  const tiers = policy?.tiers || [];
  if (tiers.length === 0) return false;
  return tiers.every(tier => (tier.days_per_year || 0) === 0);
};

// Simplified versions of remaining tabs for space efficiency
export const PoliciesTab = ({ policies, children, user, setPolicies }) => {
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    policy_type: 'pto',
    max_balance: 10,
    pay_periods_per_year: 24,
    tiers: [{ min_years: 0, max_years: '', days_per_year: '' }],
    holidays: [],
    allow_full_year_upfront: false,
    carryover_type: 'none',
    carryover_value: 0,
    accrual_cycle: 'anniversary',
    show_on_team_calendar: true
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (showModal && !editingPolicy) {
      setFormData({
        name: '',
        policy_type: 'pto',
        max_balance: 10,
        pay_periods_per_year: 24,
        tiers: [{ min_years: 0, max_years: '', days_per_year: '' }],
        holidays: [],
        allow_full_year_upfront: false,
        carryover_type: 'none',
        carryover_value: 0,
        accrual_cycle: 'anniversary',
        show_on_team_calendar: true
      });
    }
  }, [showModal, editingPolicy]);

  // Handle escape key to close modals
  useEscapeKey(() => {
    if (showModal) {
      setShowModal(false);
    }
    if (showEditModal) {
      setShowEditModal(false);
      setEditingPolicy(null);
    }
  });

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value
    }));
  };

  // Handle tier changes
  const handleTierChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      tiers: prev.tiers.map((tier, i) => 
        i === index 
          ? { ...tier, [field]: field === 'min_years' || field === 'days_per_year' 
              ? (value === '' ? '' : Number(value)) 
              : value === '' ? '' : (field === 'max_years' ? (value === '∞' ? '' : Number(value)) : value)
            }
          : tier
      )
    }));
  };

  // Add new tier
  const addTier = () => {
    setFormData(prev => ({
      ...prev,
      tiers: [...prev.tiers, { min_years: '', max_years: '', days_per_year: '' }]
    }));
  };

  // Remove tier
  const removeTier = (index) => {
    if (formData.tiers.length > 1) {
      setFormData(prev => ({
        ...prev,
        tiers: prev.tiers.filter((_, i) => i !== index)
      }));
    }
  };

  // Validate form
  const isFormValid = () => {
    if (!formData.name.trim()) return false;
    
    // At least one tier must have days_per_year filled (unless it's a usage-only sick policy)
    const hasValidTier = formData.tiers.some(tier => 
      tier.days_per_year !== '' && tier.days_per_year >= 0
    );
    
    // For sick time, allow usage-only policies (no accrual tiers)
    if (formData.policy_type === 'sick') {
      return true; // Allow creation even without accrual tiers
    }
    
    return hasValidTier;
  };

  // Handle form submission for new policy
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      alert('Please fill in the policy name and at least one valid accrual tier');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Clean up tiers - remove empty ones and format properly
      const cleanTiers = formData.tiers
        .filter(tier => tier.days_per_year !== '' || tier.min_years !== '')
        .map(tier => ({
          min_years: tier.min_years === '' ? 0 : Number(tier.min_years),
          max_years: tier.max_years === '' || tier.max_years === '∞' ? null : Number(tier.max_years),
          days_per_year: tier.days_per_year === '' ? 0 : Number(tier.days_per_year)
        }));

      const res = await fetch(`${API_BASE}/api/admin/pto_policies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': user?.email || ''
        },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name,
          policy_type: formData.policy_type,
          max_balance: Number(formData.max_balance),
          pay_periods_per_year: Number(formData.pay_periods_per_year),
          tiers: cleanTiers,
          holidays: formData.holidays,
          allow_full_year_upfront: formData.allow_full_year_upfront,
          carryover_type: formData.carryover_type,
          carryover_value: formData.carryover_value,
          accrual_cycle: formData.accrual_cycle,
          show_on_team_calendar: formData.show_on_team_calendar
        })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        // Add new policy to the list
        const newPolicy = data.policy;
        if (newPolicy && setPolicies) {
          setPolicies(prev => [...prev, newPolicy]);
        }
        
        // Close modal
        setShowModal(false);
      } else {
        throw new Error(data.error || 'Failed to create policy');
      }
    } catch (error) {
      console.error('Error creating policy:', error);
      alert(error.message || 'Error creating policy');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit policy button click
  const handleEditPolicy = (policy) => {
    setEditingPolicy(policy);
    setFormData({
      name: policy.name || '',
      policy_type: policy.policy_type || 'pto',
      max_balance: policy.max_balance || 20,
      pay_periods_per_year: policy.pay_periods_per_year || 24,
      tiers: policy.tiers && policy.tiers.length > 0 
        ? policy.tiers.map(tier => ({
            min_years: tier.min_years || 0,
            max_years: tier.max_years === null || tier.max_years === undefined ? '' : tier.max_years,
            days_per_year: tier.days_per_year || ''
          }))
        : [{ min_years: 0, max_years: '', days_per_year: '' }],
      holidays: policy.holidays || [],
      allow_full_year_upfront: policy.allow_full_year_upfront || false,
      carryover_type: policy.carryover_type || 'none',
      carryover_value: policy.carryover_value || 0,
      accrual_cycle: policy.accrual_cycle || 'anniversary',
      show_on_team_calendar:
        policy.show_on_team_calendar === undefined || policy.show_on_team_calendar === null
          ? true
          : Boolean(policy.show_on_team_calendar)
    });
    setShowEditModal(true);
  };

  // Handle form submission for editing policy
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      alert('Please fill in the policy name and at least one valid accrual tier');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const cleanTiers = formData.tiers
        .filter(tier => tier.days_per_year !== '' || tier.min_years !== '')
        .map(tier => ({
          min_years: tier.min_years === '' ? 0 : Number(tier.min_years),
          max_years: tier.max_years === '' || tier.max_years === '∞' ? null : Number(tier.max_years),
          days_per_year: tier.days_per_year === '' ? 0 : Number(tier.days_per_year)
        }));

      const res = await fetch(`${API_BASE}/api/admin/pto_policies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': user?.email || ''
        },
        credentials: 'include',
        body: JSON.stringify({
          id: editingPolicy.id || editingPolicy.name,
          name: formData.name,
          policy_type: formData.policy_type,
          max_balance: Number(formData.max_balance),
          pay_periods_per_year: Number(formData.pay_periods_per_year),
          tiers: cleanTiers,
          holidays: formData.holidays,
          allow_full_year_upfront: formData.allow_full_year_upfront,
          carryover_type: formData.carryover_type,
          carryover_value: formData.carryover_value,
          accrual_cycle: formData.accrual_cycle,
          show_on_team_calendar: formData.show_on_team_calendar
        })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        const updatedPolicy = data.policy;
        if (updatedPolicy && setPolicies) {
          setPolicies(prev => prev.map(p => 
            (p.id || p.name) === (editingPolicy.id || editingPolicy.name) 
              ? updatedPolicy 
              : p
          ));
        }
        
        setEditingPolicy(null);
        setShowEditModal(false);
      } else {
        throw new Error(data.error || 'Failed to update policy');
      }
    } catch (error) {
      console.error('Error updating policy:', error);
      alert(error.message || 'Error updating policy');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle policy deletion
  const handleDeletePolicy = async () => {
    if (!editingPolicy) return;
    
    const confirmText = window.prompt(
      `Type "DELETE" to permanently remove the policy "${editingPolicy.name}". This action cannot be undone.`
    );
    
    if (confirmText !== 'DELETE') {
      return;
    }
    
    setSubmitting(true);
    
    try {
      const policyId = editingPolicy.id || editingPolicy.name;
      const res = await fetch(`${API_BASE}/api/admin/pto_policies/${encodeURIComponent(policyId)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': user?.email || ''
        },
        credentials: 'include'
      });

      const data = await res.json();

      if (res.ok && data.success) {
        if (setPolicies) {
          setPolicies(prev => prev.filter(p => (p.id || p.name) !== policyId));
        }
        
        setEditingPolicy(null);
        setShowEditModal(false);
      } else {
        throw new Error(data.error || 'Failed to delete policy');
      }
    } catch (error) {
      console.error('Error deleting policy:', error);
      alert(error.message || 'Error deleting policy');
    } finally {
      setSubmitting(false);
    }
  };

  // Enhanced children with click handler
  const enhancedChildren = React.Children.map(children, child => {
    if (React.isValidElement(child) && child.props.className === 'primary-button') {
      return React.cloneElement(child, {
        onClick: () => setShowModal(true)
      });
    }
    return child;
  });

  // Render tier row
  const renderTierRow = (tier, index, isEditing = false) => (
    <tr key={index}>
      <td style={{ padding: '8px' }}>
        <input
          type="number"
          value={tier.min_years}
          onChange={(e) => handleTierChange(index, 'min_years', e.target.value)}
          min="0"
          step="0.1"
          style={{
            width: '100%',
            padding: '4px 8px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '14px'
          }}
          placeholder="0"
        />
      </td>
      <td style={{ padding: '8px' }}>
        <input
          type="text"
          value={tier.max_years === '' ? '∞' : tier.max_years}
          onChange={(e) => handleTierChange(index, 'max_years', e.target.value === '∞' ? '' : e.target.value)}
          style={{
            width: '100%',
            padding: '4px 8px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '14px'
          }}
          placeholder="∞"
        />
      </td>
      <td style={{ padding: '8px' }}>
        <input
          type="number"
          value={tier.days_per_year}
          onChange={(e) => handleTierChange(index, 'days_per_year', e.target.value)}
          min="0"
          step="0.5"
          style={{
            width: '100%',
            padding: '4px 8px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '14px'
          }}
          placeholder="0"
        />
      </td>
      <td style={{ padding: '8px', textAlign: 'center' }}>
        {formData.tiers.length > 1 && (
          <button
            type="button"
            onClick={() => removeTier(index)}
            style={{
              background: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '4px 8px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            Remove
          </button>
        )}
      </td>
    </tr>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 className="content-title" style={{ margin: 0 }}>Policies</h2>
        {enhancedChildren}
      </div>
      
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: 16 }}>PTO Policies</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 16 }}>
          {policies.filter(p => p.policy_type === 'pto' || !p.policy_type).map((pol) => (
            <div key={pol.id || pol.name} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, background: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <h4 style={{ margin: 0, fontSize: '1rem' }}>{pol.name}</h4>
                <button
                  onClick={() => handleEditPolicy(pol)}
                  style={{
                    background: '#F0EDE6',
                    color: 'var(--symbolica-black)',
                    border: '1px solid #d1d5db',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    borderRadius: '4px'
                  }}
                  title="Edit Policy"
                >
                  Edit
                </button>
              </div>
              <div style={{ fontSize: 14, color: 'var(--symbolica-muted)' }}>
                <div>Type: PTO</div>
                <div>Max Balance: {pol.max_balance} days</div>
                <div>Pay Periods: {pol.pay_periods_per_year}/year</div>
                <div>Tiers: {pol.tiers?.length || 0} configured</div>
                {(pol.show_on_team_calendar === false || pol.show_on_team_calendar === 0) && (
                  <div style={{ color: '#b45309' }}>Hidden from team calendar</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: 16 }}>Sick Time Policies</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 16 }}>
          {policies.filter(p => p.policy_type === 'sick').map((pol) => (
            <div key={pol.id || pol.name} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, background: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <h4 style={{ margin: 0, fontSize: '1rem' }}>{pol.name}</h4>
                <button
                  onClick={() => handleEditPolicy(pol)}
                  style={{
                    background: '#F0EDE6',
                    color: 'var(--symbolica-black)',
                    border: '1px solid #d1d5db',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    borderRadius: '4px'
                  }}
                  title="Edit Policy"
                >
                  Edit
                </button>
              </div>
              <div style={{ fontSize: 14, color: 'var(--symbolica-muted)' }}>
                <div>Type: Sick Time</div>
                {!isUsageOnlyPolicy(pol) && (
                  <div>Max Balance: {pol.max_balance} days</div>
                )}
                <div>Tiers: {pol.tiers?.length || 0} configured</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Policy Modal */}
      {showModal && (
        <Modal isOpen={showModal} onClose={() => setShowModal(false)} className="pto-policy-modal">
          <h3 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1.5rem', color: 'var(--symbolica-black)' }}>
            Add New Policy
          </h3>
          
          <form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--symbolica-black)' }}>
                Basic Information
              </h4>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                    Policy Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                    placeholder="e.g., Standard PTO Policy"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                    Policy Type
                  </label>
                  <select
                    name="policy_type"
                    value={formData.policy_type}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="pto">PTO</option>
                    <option value="sick">Sick Time</option>
                  </select>
                </div>
              </div>

              <div style={{ marginTop: '1rem' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--symbolica-black)',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    name="show_on_team_calendar"
                    checked={formData.show_on_team_calendar}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      show_on_team_calendar: e.target.checked
                    }))}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  Show on team calendar
                </label>
                <div style={{ fontSize: '12px', color: 'var(--symbolica-muted)', marginTop: '4px', marginLeft: '24px' }}>
                  When enabled, this policy's holidays and approved time off appear on the shared team calendar. Turn it off for policies assigned to only a few people (e.g. bereavement) or duplicates that would double up another policy's holidays.
                </div>
              </div>
            </div>

            {/* Accrual Configuration */}
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--symbolica-black)' }}>
                Accrual Configuration
              </h4>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                    {formData.policy_type === 'pto' ? 'Maximum Balance Cap (days)' : 'Max Balance (days)'}
                  </label>
                  <input
                    type="number"
                    name="max_balance"
                    value={formData.max_balance}
                    onChange={handleInputChange}
                    min="0"
                    step="0.5"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  <div style={{ fontSize: '12px', color: 'var(--symbolica-muted)', marginTop: '4px' }}>
                    {formData.policy_type === 'pto' ? 
                      'Absolute maximum balance an employee can have at any time. This caps both carryover and current year accrual.' : 
                      'Maximum balance that can be accrued (for usage-only policies, this is the total allowance).'}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                    Pay Periods Per Year
                  </label>
                  <input
                    type="number"
                    name="pay_periods_per_year"
                    value={formData.pay_periods_per_year}
                    onChange={handleInputChange}
                    min="1"
                    max="52"
                    step="1"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                    placeholder="24"
                  />
                  <div style={{ fontSize: '12px', color: 'var(--symbolica-muted)', marginTop: '4px' }}>
                    Common values: 12 (monthly), 24 (bi-weekly)
                  </div>
                </div>
              </div>

              {/* Accrual Cycle - Only for PTO policies */}
              {formData.policy_type === 'pto' && (
                <div style={{ marginTop: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                    Accrual Cycle
                  </label>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      fontSize: '14px',
                      color: 'var(--symbolica-black)',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="radio"
                        name="accrual_cycle"
                        value="anniversary"
                        checked={formData.accrual_cycle === 'anniversary'}
                        onChange={handleInputChange}
                        style={{ cursor: 'pointer' }}
                      />
                      Employee anniversary (start date)
                    </label>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      fontSize: '14px',
                      color: 'var(--symbolica-black)',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="radio"
                        name="accrual_cycle"
                        value="calendar"
                        checked={formData.accrual_cycle === 'calendar'}
                        onChange={handleInputChange}
                        style={{ cursor: 'pointer' }}
                      />
                      Calendar year (Jan 1 - Dec 31)
                    </label>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--symbolica-muted)', marginTop: '4px' }}>
                    {formData.accrual_cycle === 'anniversary' ? 
                      'Balance resets on the anniversary of each employee\'s start date.' : 
                      'Balance resets on January 1st for all employees. New starters receive pro-rated entitlement for their first partial year.'}
                  </div>
                </div>
              )}

              {/* Holiday Dates for PTO policies */}
              {formData.policy_type === 'pto' && (
                <div style={{ marginTop: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                    Holiday Dates (Optional)
                  </label>
                  <textarea
                    name="holidays"
                    value={Array.isArray(formData.holidays) ? formData.holidays.join('\n') : formData.holidays || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      holidays: e.target.value.split('\n').filter(date => date.trim()) 
                    }))}
                    rows="4"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'monospace',
                      resize: 'vertical'
                    }}
                    placeholder="2024-01-01&#10;2024-07-04&#10;2024-12-25"
                  />
                  <div style={{ fontSize: '12px', color: 'var(--symbolica-muted)', marginTop: '4px' }}>
                    Enter one date per line in YYYY-MM-DD format. These holidays will be excluded from PTO calculations.
                  </div>
                </div>
              )}

              {/* Allow full-year balance upfront for PTO policies */}
              {formData.policy_type === 'pto' && (
                <div style={{ marginTop: '1rem' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: 'var(--symbolica-black)',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      name="allow_full_year_upfront"
                      checked={formData.allow_full_year_upfront}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        allow_full_year_upfront: e.target.checked 
                      }))}
                      style={{
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer'
                      }}
                    />
                    Allow full-year balance upfront
                  </label>
                  <div style={{ fontSize: '12px', color: 'var(--symbolica-muted)', marginTop: '4px', marginLeft: '24px' }}>
                    When enabled, employees can book their entire annual entitlement from day 1. Balance resets yearly based on start date with no carryover.
                  </div>
                </div>
              )}

              {/* Annual Carryover Rules - Only show if not using full-year upfront */}
              {formData.policy_type === 'pto' && !formData.allow_full_year_upfront && (
                <div style={{ marginTop: '1.5rem' }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--symbolica-black)' }}>
                    Annual Carryover Rules
                  </h4>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                        Carryover Type
                      </label>
                      <select
                        name="carryover_type"
                        value={formData.carryover_type}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          carryover_type: e.target.value,
                          carryover_value: e.target.value === 'none' || e.target.value === 'unlimited' ? 0 : prev.carryover_value
                        }))}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="none">No carryover</option>
                        <option value="percentage">Percentage of unused</option>
                        <option value="fixed_days">Fixed days</option>
                        <option value="unlimited">Unlimited (up to cap)</option>
                      </select>
                    </div>

                    {(formData.carryover_type === 'percentage' || formData.carryover_type === 'fixed_days') && (
                      <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                          {formData.carryover_type === 'percentage' ? 'Percentage (%)' : 'Days'}
                        </label>
                        <input
                          type="number"
                          name="carryover_value"
                          value={formData.carryover_value}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            carryover_value: parseFloat(e.target.value) || 0 
                          }))}
                          min="0"
                          max={formData.carryover_type === 'percentage' ? "100" : undefined}
                          step={formData.carryover_type === 'percentage' ? "5" : "0.5"}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}
                          placeholder={formData.carryover_type === 'percentage' ? "50" : "10"}
                        />
                      </div>
                    )}
                  </div>

                  <div style={{ fontSize: '12px', color: 'var(--symbolica-muted)', marginTop: '8px' }}>
                    {formData.carryover_type === 'none' && 'Unused balance will be forfeited at the end of each year.'}
                    {formData.carryover_type === 'percentage' && `Employees can carry over ${formData.carryover_value || 0}% of their unused balance.`}
                    {formData.carryover_type === 'fixed_days' && `Employees can carry over up to ${formData.carryover_value || 0} days.`}
                    {formData.carryover_type === 'unlimited' && `Employees can carry over all unused balance up to the ${formData.max_balance || 0} day cap.`}
                    {formData.accrual_cycle === 'calendar' && <div style={{ marginTop: '4px', fontStyle: 'italic' }}>Note: For calendar year policies, negative balances always carry forward and reduce the next year's allocation.</div>}
                  </div>
                </div>
              )}
            </div>

            {/* Accrual Tiers */}
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--symbolica-black)' }}>
                Accrual Tiers (Days per Year)
              </h4>
              
              {formData.policy_type === 'sick' && (
                <div style={{ 
                  background: '#f0f9ff', 
                  border: '1px solid #bae6fd', 
                  borderRadius: '6px', 
                  padding: '12px', 
                  marginBottom: '1rem',
                  fontSize: '14px',
                  color: '#0369a1'
                }}>
                  <strong>Note:</strong> For usage-only sick time policies, you can leave Days/Year blank (0) to create policies that only track usage without accrual.
                </div>
              )}
              
              <table className="tiers-table" style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #d1d5db' }}>
                <thead>
                  <tr style={{ background: '#F6F4F0' }}>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Min Years</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Max Years</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Days/Year</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '1px solid #d1d5db' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.tiers.map((tier, index) => renderTierRow(tier, index))}
                </tbody>
              </table>
              
              <button
                type="button"
                onClick={addTier}
                style={{
                  marginTop: '12px',
                  background: '#F0EDE6',
                  color: 'var(--symbolica-black)',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                + Add Tier
              </button>
            </div>

            {/* Form Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '2rem' }}>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                style={{
                  background: '#F0EDE6',
                  color: 'var(--symbolica-black)',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !isFormValid()}
                style={{
                  background: (submitting || !isFormValid()) ? 'var(--symbolica-muted)' : 'var(--symbolica-black)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: (submitting || !isFormValid()) ? 'not-allowed' : 'pointer'
                }}
              >
                {submitting ? 'Creating...' : 'Add'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Policy Modal */}
      {showEditModal && editingPolicy && (
        <Modal isOpen={showEditModal} onClose={() => {
          setShowEditModal(false);
          setEditingPolicy(null);
        }} className="pto-policy-modal">
          <h3 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1.5rem', color: 'var(--symbolica-black)' }}>
            Edit Policy
          </h3>
          
          <form onSubmit={handleEditSubmit}>
            {/* Basic Information */}
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--symbolica-black)' }}>
                Basic Information
              </h4>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                    Policy Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                    placeholder="e.g., Standard PTO Policy"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                    Policy Type
                  </label>
                  <select
                    name="policy_type"
                    value={formData.policy_type}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="pto">PTO</option>
                    <option value="sick">Sick Time</option>
                  </select>
                </div>
              </div>

              <div style={{ marginTop: '1rem' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--symbolica-black)',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    name="show_on_team_calendar"
                    checked={formData.show_on_team_calendar}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      show_on_team_calendar: e.target.checked
                    }))}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  Show on team calendar
                </label>
                <div style={{ fontSize: '12px', color: 'var(--symbolica-muted)', marginTop: '4px', marginLeft: '24px' }}>
                  When enabled, this policy's holidays and approved time off appear on the shared team calendar. Turn it off for policies assigned to only a few people (e.g. bereavement) or duplicates that would double up another policy's holidays.
                </div>
              </div>
            </div>

            {/* Accrual Configuration */}
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--symbolica-black)' }}>
                Accrual Configuration
              </h4>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                    {formData.policy_type === 'pto' ? 'Maximum Balance Cap (days)' : 'Max Balance (days)'}
                  </label>
                  <input
                    type="number"
                    name="max_balance"
                    value={formData.max_balance}
                    onChange={handleInputChange}
                    min="0"
                    step="0.5"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  <div style={{ fontSize: '12px', color: 'var(--symbolica-muted)', marginTop: '4px' }}>
                    {formData.policy_type === 'pto' ? 
                      'Absolute maximum balance an employee can have at any time. This caps both carryover and current year accrual.' : 
                      'Maximum balance that can be accrued (for usage-only policies, this is the total allowance).'}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                    Pay Periods Per Year
                  </label>
                  <input
                    type="number"
                    name="pay_periods_per_year"
                    value={formData.pay_periods_per_year}
                    onChange={handleInputChange}
                    min="1"
                    max="52"
                    step="1"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                    placeholder="24"
                  />
                  <div style={{ fontSize: '12px', color: 'var(--symbolica-muted)', marginTop: '4px' }}>
                    Common values: 12 (monthly), 24 (bi-weekly)
                  </div>
                </div>
              </div>
            </div>

            {/* Accrual Cycle - Only for PTO policies */}
            {formData.policy_type === 'pto' && (
              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                  Accrual Cycle
                </label>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    fontSize: '14px',
                    color: 'var(--symbolica-black)',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="radio"
                      name="accrual_cycle"
                      value="anniversary"
                      checked={formData.accrual_cycle === 'anniversary'}
                      onChange={handleInputChange}
                      style={{ cursor: 'pointer' }}
                    />
                    Employee anniversary (start date)
                  </label>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    fontSize: '14px',
                    color: 'var(--symbolica-black)',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="radio"
                      name="accrual_cycle"
                      value="calendar"
                      checked={formData.accrual_cycle === 'calendar'}
                      onChange={handleInputChange}
                      style={{ cursor: 'pointer' }}
                    />
                    Calendar year (Jan 1 - Dec 31)
                  </label>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--symbolica-muted)', marginTop: '4px' }}>
                  {formData.accrual_cycle === 'anniversary' ? 
                    'Balance resets on the anniversary of each employee\'s start date.' : 
                    'Balance resets on January 1st for all employees. New starters receive pro-rated entitlement for their first partial year.'}
                </div>
              </div>
            )}

            {/* Holiday Dates for PTO policies */}
            {formData.policy_type === 'pto' && (
              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                  Holiday Dates (Optional)
                </label>
                <textarea
                  name="holidays"
                  value={Array.isArray(formData.holidays) ? formData.holidays.join('\n') : formData.holidays || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    holidays: e.target.value.split('\n').filter(date => date.trim()) 
                  }))}
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    resize: 'vertical'
                  }}
                  placeholder="2024-01-01&#10;2024-07-04&#10;2024-12-25"
                />
                <div style={{ fontSize: '12px', color: 'var(--symbolica-muted)', marginTop: '4px' }}>
                  Enter one date per line in YYYY-MM-DD format. These holidays will be excluded from PTO calculations.
                </div>
              </div>
            )}

            {/* Allow full-year balance upfront for PTO policies */}
            {formData.policy_type === 'pto' && (
              <div style={{ marginTop: '1rem' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: 'var(--symbolica-black)',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    name="allow_full_year_upfront"
                    checked={formData.allow_full_year_upfront}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      allow_full_year_upfront: e.target.checked 
                    }))}
                    style={{
                      width: '16px',
                      height: '16px',
                      cursor: 'pointer'
                    }}
                  />
                  Allow full-year balance upfront
                </label>
                <div style={{ fontSize: '12px', color: 'var(--symbolica-muted)', marginTop: '4px', marginLeft: '24px' }}>
                  When enabled, employees can book their entire annual entitlement from day 1. Balance resets yearly based on start date with no carryover.
                </div>
              </div>
            )}

            {/* Annual Carryover Rules - Only show if not using full-year upfront */}
            {formData.policy_type === 'pto' && !formData.allow_full_year_upfront && (
              <div style={{ marginTop: '1.5rem' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--symbolica-black)' }}>
                  Annual Carryover Rules
                </h4>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                      Carryover Type
                    </label>
                    <select
                      name="carryover_type"
                      value={formData.carryover_type}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        carryover_type: e.target.value,
                        carryover_value: e.target.value === 'none' || e.target.value === 'unlimited' ? 0 : prev.carryover_value
                      }))}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    >
                      <option value="none">No carryover</option>
                      <option value="percentage">Percentage of unused</option>
                      <option value="fixed_days">Fixed days</option>
                      <option value="unlimited">Unlimited (up to cap)</option>
                    </select>
                  </div>

                  {(formData.carryover_type === 'percentage' || formData.carryover_type === 'fixed_days') && (
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                        {formData.carryover_type === 'percentage' ? 'Percentage (%)' : 'Days'}
                      </label>
                      <input
                        type="number"
                        name="carryover_value"
                        value={formData.carryover_value}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          carryover_value: parseFloat(e.target.value) || 0 
                        }))}
                        min="0"
                        max={formData.carryover_type === 'percentage' ? "100" : undefined}
                        step={formData.carryover_type === 'percentage' ? "5" : "0.5"}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                        placeholder={formData.carryover_type === 'percentage' ? "50" : "10"}
                      />
                    </div>
                  )}
                </div>

                <div style={{ fontSize: '12px', color: 'var(--symbolica-muted)', marginTop: '8px' }}>
                  {formData.carryover_type === 'none' && 'Unused balance will be forfeited at the end of each year.'}
                  {formData.carryover_type === 'percentage' && `Employees can carry over ${formData.carryover_value || 0}% of their unused balance.`}
                  {formData.carryover_type === 'fixed_days' && `Employees can carry over up to ${formData.carryover_value || 0} days.`}
                  {formData.carryover_type === 'unlimited' && `Employees can carry over all unused balance up to the ${formData.max_balance || 0} day cap.`}
                  {formData.accrual_cycle === 'calendar' && <div style={{ marginTop: '4px', fontStyle: 'italic' }}>Note: For calendar year policies, negative balances always carry forward and reduce the next year's allocation.</div>}
                </div>
              </div>
            )}
            {/* Accrual Tiers */}
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--symbolica-black)' }}>
                Accrual Tiers (Days per Year)
              </h4>
              
              {formData.policy_type === 'sick' && (
                <div style={{ 
                  background: '#f0f9ff', 
                  border: '1px solid #bae6fd', 
                  borderRadius: '6px', 
                  padding: '12px', 
                  marginBottom: '1rem',
                  fontSize: '14px',
                  color: '#0369a1'
                }}>
                  <strong>Note:</strong> For usage-only sick time policies, you can leave Days/Year blank (0) to create policies that only track usage without accrual.
                </div>
              )}
              
              <table className="tiers-table" style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #d1d5db' }}>
                <thead>
                  <tr style={{ background: '#F6F4F0' }}>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Min Years</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Max Years</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Days/Year</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '1px solid #d1d5db' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.tiers.map((tier, index) => renderTierRow(tier, index, true))}
                </tbody>
              </table>
              
              <button
                type="button"
                onClick={addTier}
                style={{
                  marginTop: '12px',
                  background: '#F0EDE6',
                  color: 'var(--symbolica-black)',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                + Add Tier
              </button>
            </div>

            {/* Form Actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2rem' }}>
              <button
                type="button"
                onClick={handleDeletePolicy}
                disabled={submitting}
                style={{
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.6 : 1
                }}
              >
                Delete Policy
              </button>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingPolicy(null);
                  }}
                  style={{
                    background: '#F0EDE6',
                    color: 'var(--symbolica-black)',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0.75rem 1.5rem',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !isFormValid()}
                  style={{
                    background: (submitting || !isFormValid()) ? 'var(--symbolica-muted)' : 'var(--symbolica-black)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0.75rem 1.5rem',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: (submitting || !isFormValid()) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export const AdjustmentsTab = ({ adjustments, loading, children, user, setAdjustments, policies }) => {
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAdjustment, setEditingAdjustment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employeeData, setEmployeeData] = useState({});
  const [formData, setFormData] = useState({
    employee_email: '',
    policy_id: '',
    adjustment_days: 0,
    reason: '',
    adjustment_date: new Date().toISOString().split('T')[0] // Today's date
  });

  // Fetch employees when modal opens
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!showModal && !showEditModal) return;
      
      try {
        const res = await fetch(`${API_BASE}/api/admin/employees`, {
          headers: {
            'Content-Type': 'application/json',
            'X-User-Email': user?.email || ''
          },
          credentials: 'include'
        });
        if (!res.ok) throw new Error(`Failed to fetch employees: ${res.status}`);
        const data = await res.json();
        
        // Store both the employee list and the full employee data
        setEmployees(Object.keys(data.employees || {}));
        setEmployeeData(data.employees || {});
      } catch (e) {
        console.error('Error fetching employees:', e);
      }
    };
    
    fetchEmployees();
  }, [showModal, showEditModal, user]);

  // Get available policies for the selected employee
  const availablePolicies = useMemo(() => {
    if (!formData.employee_email || !employeeData[formData.employee_email]) {
      return [];
    }
    
    const selectedEmployee = employeeData[formData.employee_email];
    const employeePolicyIds = selectedEmployee.policies || [];
    
    // Filter policies to only show those assigned to the selected employee
    return policies.filter(policy => {
      const policyId = policy.id || policy.name;
      return employeePolicyIds.includes(policyId);
    });
  }, [formData.employee_email, employeeData, policies]);

  // Reset policy selection when employee changes
  useEffect(() => {
    if (formData.employee_email && formData.policy_id) {
      // Check if the currently selected policy is still available for the new employee
      const isStillAvailable = availablePolicies.some(p => 
        (p.id || p.name) === formData.policy_id
      );
      
      if (!isStillAvailable) {
        setFormData(prev => ({ ...prev, policy_id: '' }));
      }
    }
  }, [formData.employee_email, availablePolicies, formData.policy_id]);

  // Handle escape key to close modals
  useEscapeKey(() => {
    if (showModal) {
      setShowModal(false);
    }
    if (showEditModal) {
      setShowEditModal(false);
      setEditingAdjustment(null);
    }
  });

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value
    }));
  };

  // Handle edit adjustment button click
  const handleEditAdjustment = (adjustment) => {
    setEditingAdjustment(adjustment);
    setFormData({
      employee_email: adjustment.employee_email,
      policy_id: adjustment.policy_id,
      adjustment_days: adjustment.adjustment_days,
      reason: adjustment.reason,
      adjustment_date: adjustment.adjustment_date
    });
    setShowEditModal(true);
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.employee_email || !formData.policy_id || !formData.reason) {
      alert('Please fill in all required fields');
      return;
    }
    
    if (formData.adjustment_days === 0) {
      alert('Adjustment days cannot be zero');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const res = await fetch(`${API_BASE}/api/admin/balance_adjustments/${editingAdjustment.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': user?.email || ''
        },
        credentials: 'include',
        body: JSON.stringify({
          employee_email: formData.employee_email,
          policy_id: formData.policy_id,
          adjustment_days: formData.adjustment_days,
          reason: formData.reason,
          adjustment_date: formData.adjustment_date
        })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        // Update the adjustment in the list
        if (setAdjustments) {
          setAdjustments(prev => prev.map(adj => 
            adj.id === editingAdjustment.id ? data.adjustment : adj
          ));
        }
        
        // Reset form and close modal
        setFormData({
          employee_email: '',
          policy_id: '',
          adjustment_days: 0,
          reason: '',
          adjustment_date: new Date().toISOString().split('T')[0]
        });
        setShowEditModal(false);
        setEditingAdjustment(null);
      } else {
        throw new Error(data.error || 'Failed to update adjustment');
      }
    } catch (error) {
      console.error('Error updating adjustment:', error);
      alert(error.message || 'Error updating adjustment');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete adjustment
  const handleDeleteAdjustment = async (adjustment) => {
    if (!window.confirm('Are you sure you want to delete this balance adjustment?')) {
      return;
    }
    
    try {
      const res = await fetch(`${API_BASE}/api/admin/balance_adjustments/${adjustment.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': user?.email || ''
        },
        credentials: 'include'
      });

      const data = await res.json();

      if (res.ok && data.success) {
        // Remove the adjustment from the list
        if (setAdjustments) {
          setAdjustments(prev => prev.filter(adj => adj.id !== adjustment.id));
        }
      } else {
        throw new Error(data.error || 'Failed to delete adjustment');
      }
    } catch (error) {
      console.error('Error deleting adjustment:', error);
      alert(error.message || 'Error deleting adjustment');
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.employee_email || !formData.policy_id || !formData.reason) {
      alert('Please fill in all required fields');
      return;
    }
    
    if (formData.adjustment_days === 0) {
      alert('Adjustment days cannot be zero');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const res = await fetch(`${API_BASE}/api/admin/balance_adjustments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': user?.email || ''
        },
        credentials: 'include',
        body: JSON.stringify({
          employee_email: formData.employee_email,
          policy_id: formData.policy_id,
          adjustment_days: formData.adjustment_days,
          reason: formData.reason,
          adjustment_date: formData.adjustment_date
        })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        // Add new adjustment to the list
        const newAdjustment = data.adjustment;
        if (newAdjustment && setAdjustments) {
          setAdjustments(prev => [newAdjustment, ...prev]);
        }
        
        // Reset form and close modal
        setFormData({
          employee_email: '',
          policy_id: '',
          adjustment_days: 0,
          reason: '',
          adjustment_date: new Date().toISOString().split('T')[0]
        });
        setShowModal(false);
      } else {
        throw new Error(data.error || 'Failed to create adjustment');
      }
    } catch (error) {
      console.error('Error creating adjustment:', error);
      alert(error.message || 'Error creating adjustment');
    } finally {
      setSubmitting(false);
    }
  };

  // Enhanced children with click handler
  const enhancedChildren = React.Children.map(children, child => {
    if (React.isValidElement(child) && child.props.className === 'primary-button') {
      return React.cloneElement(child, {
        onClick: () => setShowModal(true)
      });
    }
    return child;
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 className="content-title" style={{ margin: 0 }}>Balance Adjustments</h2>
        {enhancedChildren}
      </div>
      
      {loading ? (
        <div>Loading adjustments…</div>
      ) : (
        <div className="table-scroll">
          <table className="emp-table">
            <thead>
              <tr>
                <th style={{ padding: 8 }}>Employee</th>
                <th style={{ padding: 8 }}>Policy</th>
                <th style={{ padding: 8 }}>Days</th>
                <th style={{ padding: 8 }}>Reason</th>
                <th style={{ padding: 8 }}>Effective Date</th>
                <th style={{ padding: 8 }}>Created By</th>
                <th style={{ padding: 8 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {adjustments.map(adj => (
                <tr key={adj.id}>
                  <td style={{ padding: 8 }}>{adj.employee_email}</td>
                  <td style={{ padding: 8 }}>{adj.policy_id}</td>
                  <td style={{ padding: 8, color: adj.adjustment_days > 0 ? '#059669' : '#dc2626' }}>
                    {adj.adjustment_days > 0 ? '+' : ''}{adj.adjustment_days}
                  </td>
                  <td style={{ padding: 8 }}>{adj.reason}</td>
                  <td style={{ padding: 8 }}>{adj.adjustment_date}</td>
                  <td style={{ padding: 8 }}>{adj.created_by}</td>
                  <td style={{ padding: 8 }}>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button 
                        className="secondary-button" 
                        style={{ fontSize: '12px', padding: '4px 8px' }}
                        onClick={() => handleEditAdjustment(adj)}
                      >
                        Edit
                      </button>
                      <button 
                        className="secondary-button" 
                        style={{ fontSize: '12px', padding: '4px 8px', backgroundColor: '#dc2626', color: 'white' }}
                        onClick={() => handleDeleteAdjustment(adj)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Adjustment Modal */}
      {showModal && (
        <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <h3 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1.5rem', color: 'var(--symbolica-black)' }}>
            Add Balance Adjustment
          </h3>
          
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                Employee *
              </label>
              <select
                name="employee_email"
                value={formData.employee_email}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              >
                <option value="">Select an employee</option>
                {employees.map(email => (
                  <option key={email} value={email}>{email}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                Policy *
              </label>
              <select
                name="policy_id"
                value={formData.policy_id}
                onChange={handleInputChange}
                required
                disabled={!formData.employee_email}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: !formData.employee_email ? '#F6F4F0' : 'white'
                }}
              >
                <option value="">
                  {!formData.employee_email ? 'Select an employee first' : 'Select a policy'}
                </option>
                {availablePolicies.map(policy => (
                  <option key={policy.id || policy.name} value={policy.id || policy.name}>
                    {policy.name} ({policy.policy_type === 'sick' ? 'Sick Time' : 'PTO'})
                  </option>
                ))}
              </select>
              {formData.employee_email && availablePolicies.length === 0 && (
                <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>
                  This employee has no assigned policies
                </div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                Adjustment Days *
              </label>
              <input
                type="number"
                name="adjustment_days"
                value={formData.adjustment_days}
                onChange={handleInputChange}
                required
                step="0.5"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
                placeholder="e.g., 5 (add) or -2 (subtract)"
              />
              <div style={{ fontSize: '12px', color: 'var(--symbolica-muted)', marginTop: '4px' }}>
                Use positive numbers to add days, negative to subtract
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                Effective Date
              </label>
              <input
                type="date"
                name="adjustment_date"
                value={formData.adjustment_date}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                Reason *
              </label>
              <textarea
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                required
                rows="3"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
                placeholder="Explain the reason for this adjustment..."
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                style={{
                  background: '#F0EDE6',
                  color: 'var(--symbolica-black)',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                style={{
                  background: submitting ? 'var(--symbolica-muted)' : 'var(--symbolica-black)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: submitting ? 'not-allowed' : 'pointer'
                }}
              >
                {submitting ? 'Creating...' : 'Create Adjustment'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Adjustment Modal */}
      {showEditModal && editingAdjustment && (
        <Modal isOpen={showEditModal} onClose={() => {
          setShowEditModal(false);
          setEditingAdjustment(null);
        }}>
          <h3 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1.5rem', color: 'var(--symbolica-black)' }}>
            Edit Balance Adjustment
          </h3>
          
          <form onSubmit={handleEditSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                Employee *
              </label>
              <select
                name="employee_email"
                value={formData.employee_email}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              >
                <option value="">Select an employee</option>
                {employees.map(email => (
                  <option key={email} value={email}>{email}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                Policy *
              </label>
              <select
                name="policy_id"
                value={formData.policy_id}
                onChange={handleInputChange}
                required
                disabled={!formData.employee_email}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: !formData.employee_email ? '#F6F4F0' : 'white'
                }}
              >
                <option value="">
                  {!formData.employee_email ? 'Select an employee first' : 'Select a policy'}
                </option>
                {availablePolicies.map(policy => (
                  <option key={policy.id || policy.name} value={policy.id || policy.name}>
                    {policy.name} ({policy.policy_type === 'sick' ? 'Sick Time' : 'PTO'})
                  </option>
                ))}
              </select>
              {formData.employee_email && availablePolicies.length === 0 && (
                <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>
                  This employee has no assigned policies
                </div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                Adjustment Days *
              </label>
              <input
                type="number"
                name="adjustment_days"
                value={formData.adjustment_days}
                onChange={handleInputChange}
                required
                step="0.5"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
                placeholder="e.g., 5 (add) or -2 (subtract)"
              />
              <div style={{ fontSize: '12px', color: 'var(--symbolica-muted)', marginTop: '4px' }}>
                Use positive numbers to add days, negative to subtract
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                Effective Date
              </label>
              <input
                type="date"
                name="adjustment_date"
                value={formData.adjustment_date}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                Reason *
              </label>
              <textarea
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                required
                rows="3"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
                placeholder="Explain the reason for this adjustment..."
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
              <button
                type="button"
                className="secondary-button"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingAdjustment(null);
                }}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="primary-button"
                disabled={submitting}
              >
                {submitting ? 'Updating...' : 'Update Adjustment'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};
